export type Action = 'allow' | 'verify' | 'review';

/** One entry in the workspace switcher dropdown. */
export interface AccessibleTenant {
  id: string;
  name: string;
  slug: string;
  /** True for the user's primary `User.tenantId` — they always have access here. */
  isPrimary: boolean;
}

/** One breakpoint in a per-agency negotiated discount ladder. */
export interface AgencyDiscountTier {
  minChildren: number;
  discountPct: number;
}

/**
 * How `action: 'verify'` records route at disposition time for one tenant. Super-admin
 * owned, set on a per-tenant basis; never exposed via tenant-facing surfaces. Mirrors
 * the union in @sieve/shared exactly.
 *
 *   - `accept`                  (default) verify-action records auto-accept.
 *   - `pending-if-not-cleared`  defer to manual review unless paid verifier said deliverable.
 *   - `pending`                 always defer to manual review.
 */
export type VerifyDispositionRule = 'accept' | 'pending-if-not-cleared' | 'pending';

/** Per-tenant detection routing override. Null on a tenant row = use the platform defaults. */
export interface DetectionConfig {
  verifyDisposition?: VerifyDispositionRule;
}

/** Response shape for /admin/tenants/:id/detection-config. */
export interface AdminDetectionConfigResponse {
  id: string;
  config: DetectionConfig | null;
  /** Effective values with defaults filled in — what's actually running for this tenant. */
  resolved: { verifyDisposition: VerifyDispositionRule };
}

/** Agency dashboard payload — child list + currently-applied + next-cliff discount. */
export interface AgencyDashboard {
  agencyTenantId: string;
  childrenCount: number;
  /** Subset of childrenCount counted toward the discount denominator (status=active only). */
  activeChildrenCount: number;
  appliedDiscount: { minChildren: number; discountPct: number } | null;
  nextDiscount: { minChildren: number; discountPct: number; childrenNeeded: number } | null;
  children: Array<{
    id: string;
    name: string;
    slug: string;
    status: 'active' | 'suspended' | 'cancelled';
    createdAt: string;
  }>;
}

/** Per-child Stripe coupon sync state, surfaced on the agency console. */
export interface AgencyChildDiscountStatus {
  tenantId: string;
  name: string;
  slug: string;
  hasSubscription: boolean;
  currentCouponId: string | null;
  expectedCouponId: string | null;
  inSync: boolean;
  error?: string;
}

export interface AgencyDiscountReapplyResult {
  appliedTier: { minChildren: number; discountPct: number } | null;
  childrenAffected: number;
  results: AgencyChildDiscountStatus[];
}

export interface AgencyChildBillingLine {
  tenantId: string;
  name: string;
  slug: string;
  status: string;
  plan: string | null;
  interval: string | null;
  baseMonthlyCents: number;
  netMonthlyCents: number;
  currency: string;
}

export interface AgencyBillingBreakdown {
  appliedDiscountPct: number;
  grossMonthlyCents: number;
  netMonthlyCents: number;
  monthlySavingsCents: number;
  currency: string;
  lines: AgencyChildBillingLine[];
}
export type VerificationStatus =
  | 'deliverable'
  | 'risky'
  | 'disposable'
  | 'spamtrap'
  | 'invalid'
  | 'unknown';

export type LegalPageKey =
  | 'terms'
  | 'privacy'
  | 'cookies'
  | 'dpa'
  | 'gdpr'
  | 'aup'
  | 'subprocessors'
  | 'contact';

/** Full visibility map returned by GET /legal/pages. Missing key = visible. */
export type LegalPagesVisibility = Record<LegalPageKey, boolean>;

export const ALL_LEGAL_PAGE_KEYS: ReadonlyArray<LegalPageKey> = [
  'terms',
  'privacy',
  'cookies',
  'dpa',
  'gdpr',
  'aup',
  'subprocessors',
  'contact',
];

/** Public social platforms we render + label (kept in sync with the API's ALL_SOCIAL_PLATFORMS). */
export type SocialPlatform =
  | 'linkedin'
  | 'x'
  | 'github'
  | 'crunchbase'
  | 'producthunt'
  | 'bluesky'
  | 'youtube'
  | 'reddit';

export interface LegalConfig {
  legalEntityName?: string;
  companyType?: string;
  vatNumber?: string;
  registrationNumber?: string;
  registeredAddress?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  supportEmail?: string;
  privacyEmail?: string;
  websiteUrl?: string;
  governingLawJurisdiction?: string;
  dataResidencyRegion?: string;
  retentionDaysAfterTermination?: number;
  subProcessors?: SubProcessor[];
  lastUpdatedAt?: string;
  /** Public social/profile URLs keyed by platform. Rendered in the footer + contact + as
   *  Organization.sameAs. Only https URLs; undefined = unset (falls back to the shipped default). */
  socialLinks?: Partial<Record<SocialPlatform, string>>;
  /**
   * When true, PublicFooter hides the business-identity block (entity name /
   * VAT / registration / registered address) and also suppresses the
   * "Business identity not yet configured — set in Admin → Settings → Legal"
   * fallback text. Falls back to a compact website-URL line if `websiteUrl`
   * is configured, otherwise renders nothing but the logo + copyright.
   */
  hideBusinessIdentityInFooter?: boolean;
  /** Super-admin toggle for the public /about page (false = hidden live via the page-gate). */
  aboutPageEnabled?: boolean;
}

export interface SubProcessor {
  name: string;
  purpose: string;
  location: string;
  transferMechanism: string;
  dpaUrl?: string;
}

export interface LandingSlide {
  id: string;
  imageUrl: string;
  headline?: string;
  subtext?: string;
  ctaText?: string;
  ctaUrl?: string;
}

export interface LandingCarouselConfig {
  enabled: boolean;
  intervalMs: number;
  slides: LandingSlide[];
}

export interface PlatformSettings {
  emailVerifier: {
    provider: 'builtin' | 'millionverifier' | 'emaillistverify';
    hasApiKey: boolean;
    /** Whether a Spamhaus DQS key is on file for the free blacklist tool. */
    hasSpamhausDqsKey: boolean;
    creditPool: number | null;
    creditAlertThresholdPct: number;
    creditAlertThresholdDays: number;
    paidVerifierEnabled: boolean;
    /** Active ELV monthly-subscription tier in credits/month; null = PAYG-only. */
    elvSubscriptionMonthlyCredits: number | null;
  };
  phoneVerifier: {
    hasApiKey: boolean;
    creditPool: number | null;
    creditsRefreshedAt: string | null;
    /** Active Veriphone subscription tier name (Hobby / Standard / Business / Premium / null). */
    planName: string | null;
    /** Monthly allowance for the active plan. -1 = unlimited. */
    monthlyLimit: number | null;
    /** PAYG credits remaining beyond the plan limit, from the last /v2/credits poll. */
    payg: number | null;
    /** Credits consumed this billing cycle, from the last poll. */
    cycleCounter: number | null;
    /** ISO datetime the plan counter next resets, from the last poll. */
    renewAt: string | null;
    /** Whether the Veriphone account is in good standing, from the last poll. */
    active: boolean | null;
    /** ISO datetime of the last counter reset, from the last poll. */
    lastResetAt: string | null;
  };
  /**
   * Per-purpose AI provider status + active model selection. Drives the AI
   * config card in /admin/settings and every consumer's "is AI ready?" gate
   * (Inbox Placement Co-Pilot, public /tools/ai-* surface, every roadmap
   * helper in ai.md).
   */
  ai: {
    anthropicConfigured: boolean;
    geminiConfigured: boolean;
    /**
     * Per-purpose provider+model. `fallbackModel` (optional, same provider) is used
     * by AiService when the primary's retry budget exhausts on a transient failure
     * — see ai-retry.ts. `null` from the API means "no fallback configured".
     */
    freeTools: {
      provider: 'anthropic' | 'gemini';
      model: string;
      fallbackModel: string | null;
      dailyLimitPerIp: number;
    };
    internal: {
      provider: 'anthropic' | 'gemini';
      model: string;
      fallbackModel: string | null;
    };
    /** Per-tenant per-month hard cap on AI spend, in USD cents. 0 = uncapped. */
    tenantMonthlyCapCentsUsd: number;
  };
  /** @deprecated kept for transitional compatibility; same as `ai.anthropicConfigured`. */
  aiCopilot: {
    hasApiKey: boolean;
  };
  /** WarmUpInbox provider status — gates the Warmup add-on platform-side. */
  warmupInbox: {
    hasApiKey: boolean;
    /** True when Google OAuth credentials are configured — gates "Connect with Google". */
    googleOauthReady: boolean;
    /** Google OAuth client id (non-secret). Null when unset. */
    googleClientId: string | null;
  };
  /** Per-feature platform-wide enable map (super-admin → Feature availability card). */
  featuresEnabled: Record<
    'blacklist' | 'inbox_placement' | 'phone_validation' | 'warmup_inbox' | 'domain_health' | 'agency',
    boolean
  >;
  /** Landing-page hero carousel config (super-admin → Landing carousel card). */
  landingCarousel: LandingCarouselConfig;
  /** Public-site analytics + SEO verification config (super-admin → Analytics). */
  analytics: {
    measurementId: string | null;
    enabled: boolean;
    cloudflareToken: string | null;
    cloudflareEnabled: boolean;
    clarityProjectId: string | null;
    clarityEnabled: boolean;
    posthogApiKey: string | null;
    posthogHost: string | null;
    posthogProxyHost: string | null;
    posthogEnabled: boolean;
  };
  trial: { days: number; credits: number; anomalyCredits: number };
  /** Ingest cut-offs — see PlatformSetting on the API for the per-field rationale. */
  ingest: {
    bulkVerifierThreshold: number;
    asyncIngestThreshold: number;
    /** Per-tenant daily force-refresh cap. null = env default (50); 0 = disabled. */
    refreshDailyCap: number | null;
  };
  legal: LegalConfig;
  systemEmail: {
    configured: boolean;
    host: string | null;
    port: number | null;
    secure: boolean;
    user: string | null;
    from: string | null;
    /** Whether a Brevo API key is on file (the key itself is never returned). */
    hasBrevoApiKey: boolean;
    /** Daily system-email cap (warn/limit threshold). Defaults to 300 when unset. */
    dailyEmailCap: number;
  };
  /** Private-beta nginx Basic Auth gate master switch — managed in Admin → Settings. */
  betaGateEnabled: boolean;
  /** Operator-set sitemap.xml <lastmod> stamp for marketing pages (YYYY-MM-DD), or null. */
  sitemapLastmod: string | null;
  /**
   * Cash-protection toggle. When TRUE, every mid-cycle addon UPGRADE is invoiced
   * + charged on the tenant's default payment method immediately (Stripe's
   * `always_invoice` + `error_if_incomplete`). When FALSE, prorations accrue and bill
   * on the next regular invoice. Read by the slider confirmation modal so the copy
   * matches the actual policy ("pay now" vs "added to next invoice").
   */
  chargeUpgradesImmediately: boolean;
  /**
   * When TRUE, /auth/register requires the user to click a verification link
   * emailed to them before /auth/login will issue tokens. When FALSE, tokens
   * are issued immediately on registration and no email is sent.
   */
  requireEmailVerification: boolean;
  /**
   * Abuse-protection config (super-admin → Settings → Abuse protection). The
   * Turnstile secret key is intentionally redacted to `hasTurnstileSecretKey`
   * (boolean) — the secret itself never leaves the server.
   */
  abuseProtection: {
    enabled: boolean;
    turnstileSiteKey: string | null;
    hasTurnstileSecretKey: boolean;
    dailyCapsPerTool: Record<string, number>;
    dailyMaxSpendCentsUsd: number;
    alertEmails: string[];
    /** Combined ELV+Veriphone 24h credit threshold per tenant — 0 disabled. */
    tenantDailyExternalCreditsThreshold: number;
    /** Header name to read the CF bot-score from — defaults to cf-bot-score. */
    botScoreHeader: string;
    /** Minimum accepted CF bot score (0–99). 0 = disabled. */
    botScoreMinAccepted: number;
  };
}

/**
 * Tenant-facing wallet shape served by GET /credit/wallet (JWT). Drives the portal
 * Subscription page's wallet card when `unifiedWalletEnabled` is on. Mirrors the API's
 * Wallet shape + adds `byFeature` per-feature spend breakdown for the inline usage row.
 */
export interface CreditWallet {
  /** Whether the wallet master switch is on. When false the rest is informational only. */
  enabled: boolean;
  /** Monthly grant from the active plan (or the global default for unsubscribed tenants). */
  planAllowance: number;
  /** Credits spent this calendar month. */
  monthlySpent: number;
  /** max(0, planAllowance - monthlySpent). */
  monthlyRemaining: number;
  /** Rolling-over balance from credit-pack purchases. Stacks on top. */
  purchasedCredits: number;
  /** monthlyRemaining + purchasedCredits — total the wallet can permit. */
  remaining: number;
  /** Allowed-feature list, or null = unrestricted (Pro plans). */
  featureGates: string[] | null;
  /** Per-feature spend this month — `{ feature: { credits, actions } }`. */
  byFeature: Record<string, { credits: number; actions: number }>;
  /** Sum of `byFeature.actions` across every feature. */
  monthlyActions: number;
}

/**
 * Tenant-facing read-only billing-mode flags served by GET /platform/billing-mode
 * (no auth). One endpoint, every public/portal surface reads it — keeps the wallet
 * vs legacy framing in lockstep across marketing, pricing, portal, and the slider
 * confirmation modal.
 */
export interface BillingMode {
  /** Mirrors `PlatformSettingsView.chargeUpgradesImmediately` — see that field. */
  chargeUpgradesImmediately: boolean;
  /**
   * When TRUE, the unified SieveGuard-Credits wallet is the active enforcement regime
   * — every billable action draws from one credit pool at the per-action cost in
   * `creditCosts`. Pricing surfaces should show "X credits / mo" + the per-action
   * cost table instead of the legacy "X verifications + Y anomaly screenings" breakdown.
   */
  unifiedWalletEnabled: boolean;
  /** Per-action credit weights. Keys match `CreditConfig.costs` on the API. */
  creditCosts: {
    detection: number;
    emailVerification: number;
    blacklist: number;
    inboxPlacement: number;
    phoneValidation: number;
    /** SPF / DKIM / DMARC / MX snapshot — 1 credit per domain by default. */
    domainHealth: number;
  };
  /** Reference retail price per 1,000 credits (cents). 0 when unconfigured. */
  referenceSaleCentsPer1000: number;
  /**
   * Live trial terms from the super-admin config (GET /platform/billing-mode). Used by the
   * homepage "how it works" story so the copy states the real offer instead of a hard-coded
   * number. `days` = platform trial length; `credits` = a new workspace's starting balance
   * (unified-wallet allotment when the wallet is on, else the legacy trial grant). Baked into
   * the prerendered HTML by the SSG prefetch.
   */
  trial: { days: number; credits: number };
}

/**
 * Public GA4 config served by GET /platform/analytics (unauthenticated). The
 * measurementId is only present when analytics is enabled AND configured, so the
 * web app can treat a null id as "do not load GA".
 */
export interface PublicAnalyticsConfig {
  measurementId: string | null;
  enabled: boolean;
  /** Cloudflare Web Analytics beacon token (cookieless — loads without consent). */
  cloudflareToken: string | null;
  /** Microsoft Clarity project id (consent-gated). */
  clarityProjectId: string | null;
  /** PostHog public key + host (consent-gated). */
  posthogApiKey: string | null;
  posthogHost: string | null;
  /** Reverse-proxy origin for ingestion, or null. When set, used as PostHog's api_host. */
  posthogProxyHost: string | null;
}

/** Cached AWS Cost Explorer snapshot shown on the super-admin cost dashboard. */
export interface AwsCostSnapshot {
  refreshedAt: string;
  currency: string;
  periodStart: string;
  monthToDateCents: number;
  forecastCents: number | null;
  byService: Array<{ service: string; cents: number }>;
}

/** Redacted AWS cost view from GET /admin/aws-cost (secret key never returned). */
export interface AwsCostView {
  configured: boolean;
  region: string;
  snapshot: AwsCostSnapshot | null;
}

/** One usage-type line under a service (GET /admin/aws-cost/usage drill-down). */
export interface AwsUsageLine {
  usageType: string;
  cents: number;
  quantity: number;
  unit: string;
}

export interface AwsUsageBreakdown {
  service: string;
  periodStart: string;
  currency: string;
  lines: AwsUsageLine[];
}

/** Redacted Stripe status from GET /admin/stripe (secrets never returned). */
export interface StripeStatus {
  enabled: boolean;
  mode: 'live' | 'test' | null;
  secretKeyConfigured: boolean;
  secretSource: 'db' | 'env' | 'none';
  webhookConfigured: boolean;
  webhookSource: 'db' | 'env' | 'none';
}

/** Per-collection tally from a Stripe catalog sync. */
export interface CatalogSyncCounts {
  created: number;
  relinked: number;
  skipped: number;
  failed: number;
}

/** Result of syncing the full catalog (plans + add-ons + credit packs + warmup tiers) to Stripe. */
export interface CatalogSyncResult {
  configured: boolean;
  mode: 'live' | 'test' | null;
  plans: CatalogSyncCounts;
  addons: CatalogSyncCounts;
  creditPacks: CatalogSyncCounts;
  warmupTiers: CatalogSyncCounts;
  message: string;
}

/** PUT /admin/stripe response — status plus the auto-sync result when a key change triggered one. */
export type StripeUpdateResponse = StripeStatus & {
  sync?: CatalogSyncResult | { error: string };
};

/** One line on the tenant's bill (plan / add-on / warmup tier / Stripe preview line). */
export interface BillingSummaryLine {
  kind: 'plan' | 'addon' | 'warmup' | 'other';
  label: string;
  unitCents: number;
  qty: number;
  /** Net line total (cents). Can be negative for proration credits in Stripe mode. */
  amountCents: number;
}

/** Real-time cost breakdown for the tenant. */
export interface BillingSummary {
  currency: string;
  status: string | null;
  interval: string | null;
  /** 'stripe' = live upcoming-invoice preview (matches the portal incl. tax + proration); 'estimate' = local, net of tax. */
  source: 'stripe' | 'estimate';
  lines: BillingSummaryLine[];
  /** Net subtotal before tax (cents). */
  subtotalCents: number;
  /** VAT on the next invoice (cents). null in estimate mode. */
  taxCents: number | null;
  /** What the next invoice charges: subtotal + tax (± proration). In estimate mode = subtotal. */
  totalCents: number;
  nextInvoiceAt: string | null;
}

/** Per-mailbox warmup tier feature switches. */
export interface WarmupTierFeatures {
  ownTemplates: boolean;
  chooseTopics: boolean;
  languageWarmup: boolean;
  customSettings: boolean;
  templateStats: boolean;
  espSpecific: boolean;
  scheduled: boolean;
}

/** Tenant-facing warmup tier for the per-mailbox picker (no wholesale/margin). */
export interface WarmupTierPublic {
  wuiPlan: 'basic' | 'pro' | 'max';
  name: string;
  blurb: string;
  maxMessagesPerDay: number;
  maxReplyRatePct: number;
  features: WarmupTierFeatures;
  retailCents: number;
  currency: string;
}

/** Super-admin view of a warmup tier (includes the private economics). */
export interface WarmupTierAdmin {
  wuiPlan: 'basic' | 'pro' | 'max';
  name: string;
  blurb: string;
  maxMessagesPerDay: number;
  maxReplyRatePct: number;
  features: WarmupTierFeatures;
  retailCents: number;
  currency: string;
  wholesaleCents: number;
  markupBps: number;
  marginCents: number;
  active: boolean;
  sortOrder: number;
  stripePriceConfigured: boolean;
}

/** One webhook endpoint on the Stripe account, from the health check. */
export interface WebhookEndpointStatus {
  url: string;
  status: string;
  wildcard: boolean;
  enabledEventCount: number;
}

/** Result of verifying the saved key's account webhook config against the required events. */
export interface WebhookVerification {
  configured: boolean;
  mode: 'live' | 'test' | null;
  endpointsFound: number;
  missingRequired: string[];
  missingRecommended: string[];
  endpoints: WebhookEndpointStatus[];
  ok: boolean;
  message: string;
}

/** One operational table in a go-live reset plan, with its current row count. */
export interface GoLiveResetPlanRow {
  table: string;
  rows: number;
}

/** What a go-live reset would wipe/keep (preview), or did wipe (with `executed: true`). */
export interface GoLiveResetPlan {
  willWipe: GoLiveResetPlanRow[];
  willKeep: string[];
  superAdminsKept: number;
  totalRows: number;
  executed?: boolean;
}

/** One day in the Email-usage trend — our Redis counters + Brevo's authoritative count. */
export interface EmailUsageDailyRow {
  date: string;
  /** Brevo's authoritative sent count for the day, or null when no Brevo data. */
  sentBrevo: number | null;
  /** Our own counter — successful sends. */
  sent: number;
  /** Our own counter — failed sends. */
  failed: number;
  /** Per-kind sent/failed for the day — drives the trend chart's hover detail. */
  byKind: Record<string, { sent: number; failed: number }>;
}

/** Super-admin Email-usage report — GET /admin/email-usage. */
export interface EmailUsageReport {
  /** The configured daily cap (default 300). */
  cap: number;
  /** Whether a Brevo API key is configured (drives the "authoritative count" note). */
  brevoConfigured: boolean;
  today: {
    /** Brevo's authoritative today count, or null when no key / no data. */
    sentBrevo: number | null;
    /** Our per-kind breakdown for today (UTC). */
    byKind: Record<string, { sent: number; failed: number }>;
  };
  daily: EmailUsageDailyRow[];
}

export interface BetaUser {
  id: string;
  username: string;
  createdByEmail: string;
  createdAt: string;
}

// ── Affiliate partner program ──────────────────────────────────────────────────
export type AffiliateStatus = 'pending' | 'active' | 'suspended';
export type AffiliateReferralStatus = 'lead' | 'active' | 'churned';
export type PayoutMethod = 'paypal' | 'wise' | 'bank' | 'other';

export interface PayoutInfo {
  method: PayoutMethod;
  data: Record<string, string>;
}

export interface AffiliateReferralRow {
  referralId: string;
  /** Client workspace name — null in the affiliate's own view (masked PII). */
  workspace: string | null;
  status: AffiliateReferralStatus;
  referredAt: string;
  firstPaidAt: string | null;
  daysActive: number | null;
  daysToNextPayout: number | null;
  commissionEarnedCents: number;
}

export interface AffiliateDashboard {
  code: string;
  status: AffiliateStatus;
  displayName: string | null;
  payout: PayoutInfo | null;
  referralLinkBase: string;
  rates: { monthlyPct: number; yearlyPct: number };
  counts: { signups: number; subscribed: number; active: number; churned: number };
  earnings: { lockedCents: number; payableCents: number; paidCents: number; currency: string };
  referrals: AffiliateReferralRow[];
}

/** One event in the affiliate portal timeline (newest first). */
export type AffiliateActivityKind =
  | 'signup'
  | 'first-paid'
  | 'invoice-paid'
  | 'plan-upgrade'
  | 'plan-downgrade'
  | 'credit-pack-purchase'
  | 'payment-failed'
  | 'mandate-inactive'
  | 'canceled'
  | 'refunded'
  | 'agency-enabled';

export interface AffiliateActivity {
  id: string;
  createdAt: string;
  kind: AffiliateActivityKind;
  tenantName: string;
  /** Money-cents for events with $ context; negative on clawbacks/refunds. */
  amountCents: number | null;
  /** Event-specific detail — see the backend enum docstring. */
  meta: Record<string, unknown> | null;
}

/** One row in the super-admin affiliates table. */
export interface AdminAffiliateRow {
  id: string;
  code: string;
  email: string;
  displayName: string | null;
  status: AffiliateStatus;
  createdAt: string;
  signups: number;
  subscribed: number;
  payableCents: number;
  paidCents: number;
}

/** Super-admin affiliate detail = the dashboard shape + identity. */
export interface AdminAffiliateDetail extends AffiliateDashboard {
  id: string;
  email: string;
}

export interface AffiliateConfig {
  enabled: boolean;
  monthlyRatePct: number;
  yearlyRatePct: number;
  /** Commission % on one-off credit pack purchases. 0 = don't pay affiliate on packs. */
  creditPackRatePct: number;
  lockDays: number;
  cookieDays: number;
  commissionMonths: number;
}

export interface BetaGateStatus {
  enabled: boolean;
  users: BetaUser[];
  /** Operator note appended to invite + reset emails while the gate is up. */
  accessNote: string;
}

/** Trial + subscription gate surfaced on /auth/me — drives every nav/gate decision. */
export interface AccessStatus {
  trialDays: number;
  trialExpiresAt: string;
  trialDaysLeft: number;
  trialActive: boolean;
  subscriptionActive: boolean;
  /** True iff either the trial OR a paid subscription is currently allowing access. */
  active: boolean;
}

export type CreditRegime = 'subscription' | 'trial' | 'expired' | 'none';

export interface CreditPurchase {
  id: string;
  amount: number;
  source: 'purchase' | 'correction';
  note: string | null;
  createdByEmail: string;
  createdAt: string;
}

export interface PackTier {
  credits: number;
  priceCents: number;
}

export interface PackRecommendation {
  monthlyConsumption: number;
  recommended: PackTier | null;
  current: PackTier | null;
  annualSavingsCents: number;
  reason: string;
}

export interface CreditRunway {
  pool: number | null;
  dailyConsumption: number;
  monthToDate: number;
  monthlyConsumption: number;
  daysOfRunway: number | null;
  alertThresholdDays: number;
  belowAlertThreshold: boolean;
  recommendation: PackRecommendation;
  currentSubscriptionCredits: number | null;
  elvRecommendation: ElvRecommendation;
  catalog: {
    subscriptions: ElvSubscriptionTier[];
    packs: PackTier[];
  };
}

export interface ElvSubscriptionTier {
  monthlyCredits: number;
  monthlyPriceEurCents: number;
}

export interface ElvRecommendation {
  monthlyConsumption: number;
  currentSubscription: ElvSubscriptionTier | null;
  recommendedSubscription: ElvSubscriptionTier | null;
  recommendedPack: PackTier;
  strategy: 'payg' | 'subscription';
  annualSavingsCents: number;
  recommendedCostPerCredit: number;
  reason: string;
}

export interface ForecastPlanCatalog {
  id: string;
  name: string;
  monthlyAmountCents: number;
  yearlyAmountCents: number | null;
  verifierCredits: number;
}

export interface ForecastCatalog {
  plans: ForecastPlanCatalog[];
  packTiers: Array<{ credits: number; priceCents: number; pricePerKEur: string }>;
}

export interface ForecastPlanMixInput {
  planId: string;
  startCustomers: number;
  growthPerMonth: number;
  yearlyShare: number;
  newYearlyShare: number;
  monthlyChurn: number;
}

export interface ForecastInput {
  months: number;
  startingCashCents: number;
  monthlyOpExCents: number;
  stripeFeePct: number;
  stripeFlatFeeCents: number;
  packTierCredits: number;
  mix: ForecastPlanMixInput[];
}

export interface ForecastMonth {
  month: number;
  customers: number;
  monthlyCustomers: number;
  yearlyCustomers: number;
  grossRevenueCents: number;
  stripeFeesCents: number;
  netRevenueCents: number;
  cogsCents: number;
  opExCents: number;
  profitCents: number;
  cumulativeCashCents: number;
  runwayMonths: number | null;
  monthlyCreditsCommitted: number;
}

export interface ForecastResult {
  months: ForecastMonth[];
  totals: {
    grossRevenueCents: number;
    stripeFeesCents: number;
    netRevenueCents: number;
    cogsCents: number;
    opExCents: number;
    profitCents: number;
    endingCashCents: number;
  };
  cashRunsOutMonth: number | null;
  breakEvenMonth: number | null;
}

export interface AdminTenantUsage {
  tenantId: string;
  name: string;
  slug: string;
  apiRequests: number;
  screenRequests: number;
  verifications: number;
  credits: number;
  creditShare: number;
  regime: CreditRegime;
  regimeAllowance: number | null;
  usedInRegime: number;
  regimePct: number | null;
}

export interface AdminKeyUsage {
  apiKeyId: string | null;
  name: string;
  keyPrefix: string | null;
  apiRequests: number;
  screenRequests: number;
}

export interface AdminTenantKeys {
  rangeDays: number;
  tenantId: string;
  keys: AdminKeyUsage[];
}

export interface AdminUsage {
  rangeDays: number;
  activeProvider: 'builtin' | 'millionverifier' | 'emaillistverify';
  paidProviderActive: boolean;
  creditPool: number | null;
  totals: {
    tenants: number;
    apiRequests: number;
    screenRequests: number;
    verifications: number;
    credits: number;
  };
  byProvider: { provider: string; total: number; billable: number; cached: number }[];
  tenants: AdminTenantUsage[];
  commitment: {
    committedMonthlyCredits: number;
    pool: number | null;
    monthsOfRunway: number | null;
    topUpNeeded: number | null;
  };
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface Me {
  id: string;
  email: string;
  role: 'super_admin' | 'super_admin_member' | 'tenant_owner' | 'tenant_member' | 'affiliate';
  status: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
    /** When true, the workspace can own child workspaces — shows the Agency nav entry + console. */
    isAgency?: boolean;
    /**
     * True iff `isAgency` AND no per-agency discount ladder has been saved yet.
     * Drives the locked /app/agency screen until a super-admin reviews and
     * negotiates a ladder for this agency.
     */
    agencyPendingReview?: boolean;
    /** Only meaningful on an agency: route every child checkout through the agency's Stripe customer. */
    consolidatedBilling?: boolean;
    /** Set when this workspace is itself a child owned by an agency. */
    parentTenantId?: string | null;
  } | null;
  /** Null for super-admins (no tenant); always populated for tenant users. */
  access: AccessStatus | null;
}

export type MemberRole = 'tenant_owner' | 'tenant_member';
export type MemberStatus = 'active' | 'invited' | 'disabled';

/** One row of the /members listing — every user attached to the calling tenant. */
export interface Member {
  id: string;
  email: string;
  role: MemberRole;
  status: MemberStatus;
  /** ISO timestamp; null after acceptance / for active users with no pending invite. */
  inviteExpiresAt: string | null;
  createdAt: string;
}

/** Preview shown by the public Accept-Invite page before asking for a password. */
export interface InvitePreview {
  email: string;
  role: MemberRole;
  tenant: {
    id: string;
    name: string;
    slug: string;
    /** When true, the workspace can own child workspaces — shows the Agency nav entry + console. */
    isAgency?: boolean;
    /** Set when this workspace is itself a child owned by an agency. */
    parentTenantId?: string | null;
  } | null;
}

export interface List {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  maxDistance: number;
  clusterThreshold: number;
  reviewMode: 'manual' | 'automated' | null;
  /** Null = inherit the tenant's reflagPriorOnDuplicate. */
  reflagPriorOnDuplicate: boolean | null;
  createdAt: string;
}

export interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** Optional auto-expiry. Null = no expiration; works until manually revoked. */
  expiresAt: string | null;
  createdAt: string;
}

export interface IssuedApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  secret: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface Verification {
  status: VerificationStatus;
  exists: boolean;
  disposable: boolean;
}

export type Disposition = 'accepted' | 'pending' | 'declined';

export type VerifierSkippedReason =
  | 'requested'
  | 'plan_no_verifier'
  | 'kill_switch_or_builtin'
  | 'quota_exhausted';

export interface EmailResult {
  id: string;
  email: string;
  valid: boolean;
  canonical: string | null;
  score: number;
  action: Action;
  disposition: Disposition;
  reasons: string[];
  nearCount: number;
  cluster: { id: string; size: number } | null;
  verification: Verification | null;
  /** Why verification didn't run. Null when verification was performed. */
  verifierSkippedReason: VerifierSkippedReason | null;
}

export interface ReviewRecord {
  id: string;
  email: string;
  canonical: string | null;
  score: number;
  action: Action;
  disposition: Disposition;
  reasons: string[];
  nearCount: number;
  verificationStatus: VerificationStatus | null;
  reviewNote: string | null;
  createdAt: string;
}

/**
 * One sibling row on the record-detail page — another record (same tenant + list) whose
 * canonical form matches. Drives the "Same mailbox" panel and the retroactive-flag
 * banner when an accepted earlier record turns out to be part of a duplicate pattern.
 */
export interface RecordSibling {
  id: string;
  email: string;
  score: number;
  action: Action;
  disposition: Disposition;
  verificationStatus: VerificationStatus | null;
  createdAt: string;
}

export interface RecordDetail extends ReviewRecord {
  clusterId: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  source: string;
  domain: string | null;
  siblings?: RecordSibling[];
  /** Embedded Domain Health snapshot when the workspace has access. */
  domainHealth?: RecordDomainHealth | null;
  localPart: string | null;
}

export interface NotificationChannel {
  id: string;
  type: 'slack' | 'email' | 'intercom';
  name: string;
  /** Which events fire alerts on this channel — values from CHANNEL_EVENTS. */
  events: string[];
  /** Sub-filter applied only when the channel subscribes to `emails.scored`. */
  dispositions: string[];
  active: boolean;
  createdAt: string;
}

/**
 * One row per event the backend's WEBHOOK_EVENTS registry exposes. Drives BOTH
 * the webhook-endpoint picker on /app/webhooks and the notification-channel
 * picker on /app/notifications — `alertable` decides which events show in the
 * channel picker (the alertable subset of webhook events).
 *
 * Keep in sync with apps/api/src/webhooks/events.ts. When a new event ships on
 * the backend, add the row here so the portal picker shows it with a label +
 * hint instead of the raw event name.
 */
export interface WebhookEventMeta {
  value: string;
  label: string;
  hint: string;
  alertable: boolean;
}

export const WEBHOOK_EVENT_CATALOGUE: ReadonlyArray<WebhookEventMeta> = [
  {
    value: 'emails.scored',
    label: 'Email scored',
    hint: 'Batch verification finished. Sub-filter by disposition below.',
    alertable: true,
  },
  {
    value: 'email.reviewed',
    label: 'Email reviewed',
    hint: 'A pending record was accepted or declined manually.',
    alertable: true,
  },
  {
    value: 'email.duplicate_detected',
    label: 'Email duplicate detected',
    hint: 'A new address folded onto a canonical that already had prior records (alias gaming, +tag stripping). Webhook only — too granular for alerting.',
    alertable: false,
  },
  {
    value: 'inbox_placement.test_completed',
    label: 'Inbox Placement test completed',
    hint: 'An async placement test transitioned from running → complete.',
    alertable: true,
  },
  {
    value: 'inbox_placement.reputation_regressed',
    label: 'Inbox Placement reputation regressed',
    hint: '30-day trend dropped ≥ 5 points vs the prior half — likely a sliding-reputation event.',
    alertable: true,
  },
  {
    value: 'phone_validation.bulk_completed',
    label: 'Phone bulk job completed',
    hint: 'An async bulk-CSV phone job hit the completed stage.',
    alertable: true,
  },
  {
    value: 'verifier_credit.threshold_crossed',
    label: 'Verifier credits near limit',
    hint: 'Monthly verifier-credit usage crossed the alert threshold (default 80%).',
    alertable: true,
  },
  {
    value: 'warmup_inbox.added',
    label: 'Warming mailbox added',
    hint: 'A new warming mailbox was provisioned (via the API or portal).',
    alertable: true,
  },
  {
    value: 'warmup_inbox.paused',
    label: 'Warming mailbox paused',
    hint: 'A warming mailbox transitioned to paused. Slot stays reserved.',
    alertable: true,
  },
  {
    value: 'warmup_inbox.resumed',
    label: 'Warming mailbox resumed',
    hint: 'A paused warming mailbox was started again.',
    alertable: true,
  },
  {
    value: 'warmup_inbox.deleted',
    label: 'Warming mailbox deleted',
    hint: 'A warming mailbox was removed and its slot freed.',
    alertable: true,
  },
  {
    value: 'warmup_inbox.warmed_up',
    label: 'Warming mailbox warmed up',
    hint: 'Reputation crossed the warmed-up threshold (default 80) for the first time. Fires once per mailbox lifecycle.',
    alertable: true,
  },
  {
    value: 'warmup_inbox.reputation_dropped',
    label: 'Warming mailbox reputation dropped',
    hint: 'Reputation regressed by ≥ 10 points vs the prior sync. Urgent — likely a sender-reputation event.',
    alertable: true,
  },
  {
    value: 'warmup_inbox.daily_report',
    label: 'Warming mailbox daily report',
    hint: 'Per-mailbox snapshot fired by each sync. Webhook only — would flood a Slack channel for tenants with many mailboxes.',
    alertable: false,
  },
  {
    value: 'billing.addon_tier_changed',
    label: 'Add-on tier changed',
    hint: 'An add-on slider moved. Webhook only — you just did this yourself, so alerting would be self-noise.',
    alertable: false,
  },
  {
    value: 'billing.plan_changed',
    label: 'Plan changed',
    hint: 'Subscription plan or billing interval changed (upgrade, downgrade, or month↔year switch).',
    alertable: true,
  },
  {
    value: 'billing.invoice_paid',
    label: 'Invoice paid',
    hint: 'Stripe confirmed a payment succeeded — finance-team confirmation alert.',
    alertable: true,
  },
  {
    value: 'billing.invoice_failed',
    label: 'Invoice payment failed',
    hint: 'Stripe reported a failed payment — urgent. Comes with the decline reason in the payload.',
    alertable: true,
  },
  {
    value: 'billing.invoice_upcoming',
    label: 'Invoice upcoming',
    hint: "Stripe's ~72h heads-up before an invoice draws. Requires the invoice.upcoming event to be enabled on the Stripe dashboard.",
    alertable: true,
  },
  {
    value: 'domain.blacklisted',
    label: 'Domain blacklisted',
    hint: 'A previously-clean domain just landed on a DNSBL. Sending-reputation crisis — fires only on the transition.',
    alertable: true,
  },
  {
    value: 'domain_health.score_changed',
    label: 'Domain Health score dropped a band',
    hint: 'Composite score (SPF / DKIM / DMARC / MX) crossed a band boundary downward (75 → 50, 50 → 25). Upward recoveries stay silent.',
    alertable: true,
  },
  {
    value: 'quota.threshold_crossed',
    label: 'Feature quota threshold crossed',
    hint: 'Monthly usage crossed 80% or 100% of allowance on a feature (Inbox Placement, Phone Validation, Blacklist checks). Fires at most once per feature per month per threshold.',
    alertable: true,
  },
];

/**
 * Alertable subset of WEBHOOK_EVENT_CATALOGUE — drives the
 * notification-channel editor on /app/notifications. Mirrors the API-side
 * CHANNEL_EVENTS constant in apps/api/src/webhooks/events.ts.
 */
export const CHANNEL_EVENTS: ReadonlyArray<WebhookEventMeta> = WEBHOOK_EVENT_CATALOGUE.filter(
  (e) => e.alertable,
);

export interface TenantSettings {
  reviewMode: 'manual' | 'automated';
  billingEmail?: string | null;
  name: string;
  slug: string;
  /** EU VAT ID, e.g. "MT12345678". Null if unset. */
  vatId?: string | null;
  /** ISO 3166-1 alpha-2 billing country, e.g. "MT". Null if unset. */
  country?: string | null;
  /**
   * Full billing address — written to the Stripe customer so invoices have a complete
   * "Bill To" block and Stripe Tax / Radar can refine their jurisdictional lookup.
   * All nullable so existing tenants keep working with just `country` until they fill
   * in the rest. `state` only matters for US / CA / AU.
   */
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  postalCode?: string | null;
  state?: string | null;
  /**
   * When ON, the engine reopens prior `accepted` records (same canonical mailbox) into
   * the review queue when a later submission is detected as an exact-alias duplicate.
   * Per-list override may pin a different value.
   */
  reflagPriorOnDuplicate?: boolean;
  /** Domain Health add-on toggle — gates the /app/domains/health page + per-record badges. */
  domainHealthEnabled?: boolean;
  /** Super-admin grant of the Domain Health add-on for Basic plans. Read-only on the tenant side. */
  domainHealthAddonActive?: boolean;
  /** Ingest-time cache warming for every newly-seen domain. */
  domainHealthAutoWarm?: boolean;
  /** Tenant-picked ELV blacklist-check add-on tier (0/10/50/100/500/1000 per month). */
  blacklistChecksAddon?: number;
  /** Inbox-placement-test add-on tier (0 / N per month). Wired later. */
  inboxPlacementTestsAddon?: number;
  /** Veriphone phone-validation add-on tier (0/100/500/1k/5k/10k per month). */
  phoneValidationAddon?: number;
  /** WarmUpInbox slot bundle (0 / 1 / 3 / 5 / 10 / 25 / 50 concurrent inboxes). */
  warmupInboxesAddon?: number;
}

export type AddonKind = 'blacklist' | 'inbox_placement' | 'phone_validation' | 'warmup_inbox';

export interface AddonTier {
  quota: number;
  monthlyCents: number;
  label: string;
}

export interface AddonTierFromApi {
  addon: AddonKind;
  quota: number;
  label: string;
  monthlyAmountCents: number;
}

/**
 * Full admin view of an addon_prices row — includes archived rows and Stripe IDs.
 * Editable from /admin/addon-prices.
 */
export interface AdminAddonPrice {
  id: string;
  addon: AddonKind;
  quota: number;
  label: string;
  monthlyAmountCents: number;
  currency: string;
  stripePriceId: string | null;
  stripeProductId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * One-time-purchase credit pack the super-admin configures and tenants buy from
 * Billing → Credit Packs (Phase 4). Maps 1:1 to a Stripe Product + one-time Price.
 * On successful purchase the webhook increments Tenant.purchasedCredits.
 */
export interface CreditPack {
  id: string;
  label: string;
  description: string | null;
  credits: number;
  priceCents: number;
  currency: string;
  sortOrder: number;
  active: boolean;
  stripeProductId: string | null;
  stripePriceId: string | null;
}

/**
 * @deprecated Use the live API tier list (`GET /billing/addons/tiers` for JWT,
 * `GET /plans/addons` for public) — these constants are kept only as a fallback when
 * the API is unreachable and as documentation of the seed-time defaults. Anything
 * the super-admin edits on /admin/addon-prices flows through the API; reading these
 * constants would show stale numbers.
 */
export const BLACKLIST_TIERS: Array<AddonTier> = [
  { quota: 0, monthlyCents: 0, label: 'Off' },
  { quota: 10, monthlyCents: 99, label: 'Starter' },
  { quota: 50, monthlyCents: 399, label: 'Basic' },
  { quota: 100, monthlyCents: 749, label: 'Standard' },
  { quota: 500, monthlyCents: 2999, label: 'Growth' },
  { quota: 1000, monthlyCents: 4999, label: 'Volume' },
];

/**
 * Inbox-placement-test slider tiers. 100 ELV credits per test, so the per-test sell
 * price runs €1.49 → €0.80 as volume rises. Mirrors the allowed set on the API.
 */
export const INBOX_PLACEMENT_TIERS: Array<{ quota: number; monthlyCents: number; label: string }> = [
  { quota: 0,   monthlyCents: 0,    label: 'Off' },
  { quota: 1,   monthlyCents: 149,  label: 'Trial' },
  { quota: 5,   monthlyCents: 599,  label: 'Starter' },
  { quota: 25,  monthlyCents: 2499, label: 'Basic' },
  { quota: 50,  monthlyCents: 4499, label: 'Pro' },
  { quota: 100, monthlyCents: 7999, label: 'Volume' },
];

export interface InboxPlacementAccess {
  canUse: boolean;
  monthlyQuota: number;
  usedThisMonth: number;
  remaining: number;
  source: 'plan' | 'addon' | 'mixed' | null;
  planQuota: number;
  addonQuota: number;
}

export interface InboxPlacementRecipient {
  email: string;
  esp: 'google' | 'yahoo' | 'outlook' | 'zoho' | 'other';
  type: 'personal' | 'professional';
  placement: 'inbox' | 'category' | 'spam' | 'waiting' | 'missing';
  foundAt: string | null;
}

export interface InboxPlacementTest {
  id: string;
  elvCode: string;
  elvId: string | null;
  name: string;
  seedEmails: string[];
  status: 'running' | 'complete' | 'failed' | 'expired';
  recipients: InboxPlacementRecipient[] | null;
  summary: {
    inbox: number;
    promotions: number;
    spam: number;
    waiting: number;
    missing: number;
  } | null;
  detectedSender: string | null;
  senderDomain: string | null;
  /** Composite 0-100 score derived from the summary tiles. Null until the test surfaces data. */
  deliverabilityScore: { value: number; band: 'excellent' | 'good' | 'mixed' | 'poor' | null } | null;
  /** Joined Domain Health snapshot for the detected sender's domain (DIY DNS — free). */
  senderDomainHealth: {
    score: number;
    spfStatus: string;
    spfPolicy: string | null;
    dkimStatus: string;
    dmarcStatus: string;
    dmarcPolicy: string | null;
    blacklistHits: number | null;
    blacklistCheckedAt: string | null;
    checkedAt: string;
  } | null;
  lastPolledAt: string | null;
  completedAt: string | null;
  createdAt: string;
  /** 'manual' (tenant sends) or 'auto' (we sent via their SMTP). */
  sendMode?: 'manual' | 'auto';
  /** Auto-send outcome — present only on auto-mode tests. */
  autoSend?: {
    provider: string;
    sent: number;
    failed: number;
    failures?: string[];
    at: string;
  } | null;
}

export interface InboxPlacementList {
  total: number;
  rows: InboxPlacementTest[];
}

/** Redacted auto-send SMTP config returned by GET /inbox-placement/smtp. */
export interface InboxSmtpView {
  configured: boolean;
  provider: 'smtp';
  preset: string | null;
  host: string | null;
  port: number | null;
  secure: boolean;
  user: string | null;
  fromAddress: string | null;
  fromName: string | null;
  hasPassword: boolean;
}

/** A reusable inbox-placement sending identity (per-mailbox SMTP), password redacted. */
export interface InboxMailbox {
  id: string;
  label: string;
  smtp: InboxSmtpView;
  lastTestAt: string | null;
  createdAt: string;
}

export interface TrendAggregate {
  averageScore: number | null;
  band: 'excellent' | 'good' | 'mixed' | 'poor' | null;
  testsCount: number;
  delta: number | null;
  direction: 'up' | 'down' | 'flat' | null;
}

export interface InboxPlacementTrend {
  rangeDays: number;
  since: string;
  overall: TrendAggregate;
  bySender: Array<{ senderDomain: string } & TrendAggregate>;
  weekly: Array<{
    weekStart: string;
    averageScore: number | null;
    testsCount: number;
  }>;
}

/**
 * Phone Validation slider tiers (Veriphone-backed). Each lookup costs Veriphone
 * ~€0.0015 at scale, so every published price holds at 88–95% gross margin. The
 * shape mirrors BLACKLIST_TIERS / INBOX_PLACEMENT_TIERS to keep the slider component
 * generic.
 */
export const PHONE_VALIDATION_TIERS: Array<{ quota: number; monthlyCents: number; label: string }> = [
  { quota: 0,      monthlyCents: 0,     label: 'Off' },
  { quota: 100,    monthlyCents: 299,   label: 'Starter' },
  { quota: 500,    monthlyCents: 999,   label: 'Basic' },
  { quota: 1000,   monthlyCents: 1799,  label: 'Standard' },
  { quota: 5000,   monthlyCents: 6999,  label: 'Growth' },
  { quota: 10000,  monthlyCents: 12999, label: 'Volume' },
];

/**
 * Warmup-inbox slot ladder fallback. Prices default to 0 until WUI wholesale numbers
 * land — the slider then shows tier labels with a "configure pricing" hint instead
 * of bogus €0 prices. Once the super-admin seeds prices via /admin/addon-prices these
 * fallbacks are bypassed entirely.
 */
export const WARMUP_INBOX_TIERS: Array<{ quota: number; monthlyCents: number; label: string }> = [
  { quota: 0,  monthlyCents: 0, label: 'Off' },
  { quota: 1,  monthlyCents: 0, label: 'Starter' },
  { quota: 3,  monthlyCents: 0, label: 'Pair' },
  { quota: 5,  monthlyCents: 0, label: 'Team' },
  { quota: 10, monthlyCents: 0, label: 'Growth' },
  { quota: 25, monthlyCents: 0, label: 'Volume' },
  { quota: 50, monthlyCents: 0, label: 'Agency' },
];

export interface PhoneValidationAccess {
  canUse: boolean;
  monthlyQuota: number;
  usedThisMonth: number;
  remaining: number;
  source: 'plan' | 'addon' | 'mixed' | null;
  planQuota: number;
  addonQuota: number;
  /** False when the platform-side Veriphone API key isn't configured. */
  providerReady: boolean;
}

export type PhoneStatus = 'valid' | 'invalid' | 'unknown' | 'syntax-error';
export type PhoneType =
  | 'mobile'
  | 'fixed_line'
  | 'toll_free'
  | 'premium_rate'
  | 'shared_cost'
  | 'voip'
  | 'unknown';

export interface PhoneValidation {
  id: string;
  rawInput: string;
  e164: string;
  phoneStatus: PhoneStatus;
  phoneType: PhoneType | null;
  countryCode: string | null;
  countryName: string | null;
  countryPrefix: string | null;
  carrier: string | null;
  internationalNumber: string | null;
  localNumber: string | null;
  billable: boolean;
  createdAt: string;
}

export interface PhoneValidationList {
  total: number;
  rows: PhoneValidation[];
}

/** Aggregated cache metrics for the Phone Validations Analytics tab. */
export interface PhoneValidationAnalytics {
  total: number;
  cached: number;
  billed: number;
  status: { valid: number; invalid: number; unknown: number; 'syntax-error': number };
  type: Record<string, number>;
  topCountries: Array<{ code: string; name: string; count: number }>;
  window: {
    current: { total: number };
    previous: { total: number };
  };
  daily: Array<{ day: string; total: number; valid: number; invalid: number }>;
}

/** Effective Domain Health access for the workspace. */
export interface DomainHealthAccess {
  canUse: boolean;
  hasAccess: boolean;
  toggleOff: boolean;
  source: 'plan' | 'addon' | 'trial' | null;
}

export type WuiPlan = 'basic' | 'pro' | 'max' | 'send_only' | 'monitoring' | 'free_monitoring';

export type WuiStatus = 'paused' | 'running' | 'banned' | 'error' | 'suspended';
export type WuiInboxType = 'google' | 'office' | 'custom';

/** Effective Warmup access for the workspace. */
export interface WarmupInboxAccess {
  canUse: boolean;
  slotCap: number;
  slotsUsed: number;
  slotsRemaining: number;
  source: 'plan' | 'addon' | 'mixed' | null;
  planSlots: number;
  addonSlots: number;
  wuiPlan: WuiPlan;
  providerReady: boolean;
  /** Whether platform-side Google OAuth credentials are configured. */
  googleOauthReady: boolean;
  /**
   * TRUE when the tenant is on a Basic plan (or has no subscription). Warmup is
   * Pro-only, so the UI reads this to render an "Upgrade to Pro" upsell instead
   * of the generic "no slots" empty state.
   */
  requiresProPlan: boolean;
}

/** One cached warming inbox row. */
export interface WarmupInbox {
  id: string;
  wuiInboxId: string;
  email: string;
  wuiPlan: WuiPlan;
  inboxType: WuiInboxType;
  status: WuiStatus;
  reputationScore: number | null;
  reputationByEsp: { google: number; microsoft: number; other: number } | null;
  senderFirst: string | null;
  senderLast: string | null;
  timezone: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
}

/** Supported warmup-content languages per WUI. */
export type WuiLanguage =
  | 'english' | 'german' | 'french' | 'spanish' | 'polish' | 'italian' | 'dutch'
  | 'danish' | 'finnish' | 'portuguese' | 'slovak' | 'russian' | 'swedish'
  | 'norwegian' | 'turkish' | 'indonesian' | 'ukrainian' | 'romanian' | 'czech'
  | 'hungarian' | 'malay' | 'bulgarian' | 'serbian';

export interface WuiEspPriority {
  google: boolean;
  outlook: boolean;
  all_other: boolean;
}

export interface WuiSchedulePeriod {
  start: number;
  end: number;
}

export interface WuiSchedule {
  mon?: WuiSchedulePeriod | null;
  tue?: WuiSchedulePeriod | null;
  wed?: WuiSchedulePeriod | null;
  thu?: WuiSchedulePeriod | null;
  fri?: WuiSchedulePeriod | null;
  sat?: WuiSchedulePeriod | null;
  sun?: WuiSchedulePeriod | null;
}

export interface WuiInboxFrequency {
  starting_baseline: number;
  increase_per_day: number;
  max_sends_per_day: number;
  reply_rate: number;
  strategy?: 'progressive' | 'flat' | 'random';
}

/** Email template owned by the tenant (via inbox association on the WUI side). */
export interface WarmupTemplate {
  id: string;
  subject: string;
  body: string;
  /** WUI inbox ids (not our local UUIDs) — the list page resolves them via the inbox list. */
  inboxes: string[];
  preference: number;
  isActive: boolean;
  type: 'html' | 'text';
  stats?: { spam: number; category: number; inbox: number };
}

export interface WarmupTemplatesPage {
  items: WarmupTemplate[];
  current_page: number;
  total_pages: number;
  total: number;
}

/** Live provider-side details — read on demand from /warmup-inbox/:id/details. */
export interface WarmupInboxDetails {
  inbox_id: string;
  email: string;
  status: WuiStatus;
  type: WuiInboxType;
  plan: WuiPlan;
  reputation?: { score: number; score_google: number; score_microsoft: number; score_all_other: number };
  frequency?: WuiInboxFrequency;
  schedule?: WuiSchedule;
  language?: WuiLanguage;
  esp_priority?: WuiEspPriority;
  email_topics?: string[];
  extended_reply?: boolean;
  sender_first?: string;
  sender_last?: string;
  timezone?: string;
  health_check?: {
    mx?: { score: number; last_check_at: string; records: Array<{ host: string; priority: number }> };
    spf?: { score: number; last_check_at: string; record?: string };
    dmarc?: { score: number; last_check_at: string; record?: string };
    domain_blacklists?: {
      score: number;
      last_check_at: string;
      blacklists: Array<{ name: string; url: string; detected: boolean }>;
    };
    warmup_days?: { score: number; last_check_at: string; days: number };
  };
}

/** Generic bulk-operation result — used by every /warmup-inbox/bulk/* endpoint. */
export interface WarmupBulkResult<T = unknown> {
  succeeded: T[];
  failed: Array<{ index: number; id?: string; email?: string; error: string }>;
}

/** One parsed CSV row the bulk-connect form sends to /warmup-inbox/bulk/connect. */
export interface WarmupBulkSmtpRow {
  email: string;
  senderFirst: string;
  senderLast: string;
  timezone?: string;
  smtpHost: string;
  smtpPort: number;
  smtpTls: boolean;
  smtpUser: string;
  smtpPass: string;
  imapHost: string;
  imapPort: number;
  imapTls: boolean;
  imapUser: string;
  imapPass: string;
}

/** Per-inbox metrics — pulled live from WUI, not cached. */
export interface WarmupInboxMetrics {
  inbox_id: string;
  start_time: number;
  end_time: number;
  main_metrics: {
    total_count: number;
    sent: number;
    temperature: number;
    landed_inbox: { count: number };
    landed_spam: { count: number };
    landed_category: { count: number };
    replies_received: number;
  };
  schedule_metrics: Array<{
    date: string;
    queued: number;
    inbox: number;
    category: number;
    spam: number;
    replies_received: number;
  }>;
}

/** Cross-tenant WarmUpInbox runway + margin view for the super-admin BO. */
export interface WarmupRunway {
  providerReady: boolean;
  wholesaleConfigured: boolean;
  markup: number;
  perPlan: Array<{
    plan: WuiPlan;
    total: number;
    available: number;
    inUse: number;
    wholesaleCentsPerSlot: number;
    monthlyWholesaleCents: number;
  }>;
  totals: {
    totalSlots: number;
    inUseSlots: number;
    availableSlots: number;
    monthlyWholesaleCents: number;
    monthlyRetailCents: number | null;
    monthlyMarginCents: number | null;
    grossMarginPct: number | null;
  };
  byTenant: Array<{
    tenantId: string;
    tenantName: string;
    slotCount: number;
    wuiPlan: WuiPlan;
    monthlyRetailCents: number;
    monthlyWholesaleCents: number;
  }>;
}

/** Combined Domain Health + Blacklist analytics. */
export interface DomainHealthAnalytics {
  totalDomains: number;
  averageScore: number | null;
  scoreBands: { excellent: number; good: number; mixed: number; poor: number };
  spf: { present: number; missing: number; multiple: number; invalid: number };
  dkim: { present: number; missing: number };
  dmarc: {
    status: { present: number; missing: number; invalid: number };
    policy: { reject: number; quarantine: number; none: number; unset: number };
  };
  topWeakDomains: Array<{ domain: string; score: number; checkedAt: string }>;
  blacklist: {
    domainsChecked: number;
    domainsFlagged: number;
    totalListings: number;
    topListings: Array<{ name: string; domain: string | null; url: string | null; count: number }>;
    recentlyFlagged: Array<{ domain: string; hits: number; checkedAt: string }>;
  };
  window: {
    current: { added: number };
    previous: { added: number };
  };
}

export interface PhoneBulkJob {
  id: string;
  veriphoneFileId: string;
  name: string;
  status: 'uploaded' | 'ready' | 'verifying' | 'completed' | 'failed' | 'expired';
  rowsTotal: number;
  rowsBillable: number;
  rowsValid: number;
  rowsInvalid: number;
  rowsSyntaxError: number;
  positionBytes: number;
  sizeBytes: number;
  lastPolledAt: string | null;
  completedAt: string | null;
  failureReason: string | null;
  createdAt: string;
}

export interface PhoneBulkJobList {
  total: number;
  rows: PhoneBulkJob[];
}

export interface DomainHealthRow {
  id: string;
  domain: string;
  spfStatus: 'present' | 'missing' | 'multiple' | 'invalid';
  spfRecord: string | null;
  spfPolicy: '-all' | '~all' | '?all' | '+all' | null;
  dkimStatus: 'present' | 'missing';
  dkimSelectors: string[];
  dmarcStatus: 'present' | 'missing' | 'invalid';
  dmarcRecord: string | null;
  dmarcPolicy: 'none' | 'quarantine' | 'reject' | null;
  dmarcPct: number | null;
  mxRecords: string[];
  /** Number of DNSBLs that flagged this domain on the most recent ELV check; null = check didn't run. */
  blacklistHits: number | null;
  blacklistListings: Array<{ name: string; url: string; domain: string }> | null;
  blacklistCheckedAt: string | null;
  score: number;
  error: string | null;
  checkedAt: string;
  expiresAt: string;
}

export interface DomainHealthList {
  total: number;
  rows: DomainHealthRow[];
}

/** Tenant's Domain Health access + the blacklist-check allowance/usage (GET /domain-health/access). */
export interface DomainHealthAccess {
  canUse: boolean;
  hasAccess: boolean;
  toggleOff: boolean;
  source: 'plan' | 'addon' | 'trial' | null;
  blacklist: {
    /** Blacklist sub-feature is on AND the tenant has a monthly check allowance. */
    enabled: boolean;
    monthlyQuota: number;
    usedThisMonth: number;
    remaining: number;
    planQuota: number;
    addonQuota: number;
  };
}

export type PnlCategory =
  | 'revenue_subscription'
  | 'cogs_elv'
  | 'cogs_veriphone'
  | 'cogs_warmup_inbox'
  | 'cogs_stripe_fees'
  | 'salary'
  | 'hosting'
  | 'marketing'
  | 'software'
  | 'professional'
  | 'tax'
  | 'other';

export type PnlSource = 'manual' | 'stripe' | 'elv' | 'veriphone' | 'recurring' | 'computed';

/** Categories the super-admin can pick when creating a manual / recurring entry. */
export const MANUAL_PNL_CATEGORIES: PnlCategory[] = [
  'cogs_stripe_fees',
  'salary',
  'hosting',
  'marketing',
  'software',
  'professional',
  'tax',
  'other',
];

export interface PnlEntry {
  id: string;
  category: PnlCategory;
  label: string;
  amountCents: number;
  source: PnlSource;
  notes: string | null;
  refId: string | null;
  createdAt: string;
}

export interface PnlMonth {
  year: number;
  month: number;
  periodMonth: string;
  revenueCents: number;
  cogsCents: number;
  grossProfitCents: number;
  grossMarginPct: number | null;
  operatingCents: number;
  netProfitCents: number;
  netMarginPct: number | null;
  /** ELV credit cost avoided by serving blacklist checks via Spamhaus first (informational). */
  spamhausSavingsCents: number;
  byCategory: Record<PnlCategory, number>;
  entries: PnlEntry[];
}

export interface PnlYear {
  year: number;
  months: PnlMonth[];
  ytd: {
    revenueCents: number;
    cogsCents: number;
    operatingCents: number;
    grossProfitCents: number;
    netProfitCents: number;
    spamhausSavingsCents: number;
  };
}

export interface PnlRecurring {
  id: string;
  category: PnlCategory;
  label: string;
  amountCents: number;
  startMonth: string;
  endMonth: string | null;
  active: boolean;
  notes: string | null;
  createdAt: string;
}

export interface PnlForecastMonth {
  year: number;
  month: number;
  periodMonth: string;
  /** False when projected (i.e. current month onwards). */
  isActual: boolean;
  revenueCents: number;
  cogsCents: number;
  operatingCents: number;
  netProfitCents: number;
  cumulativeNetProfitCents: number;
}

export interface PnlForecastYear {
  year: number;
  revenueCents: number;
  cogsCents: number;
  operatingCents: number;
  netProfitCents: number;
  grossMarginPct: number | null;
  netMarginPct: number | null;
  actualMonths: number;
  projectedMonths: number;
}

export interface PnlForecast {
  startYear: number;
  endYear: number;
  months: PnlForecastMonth[];
  yearSummaries: PnlForecastYear[];
  baseline: {
    revenueCents: number;
    /** Average ELV COGS as a fraction of revenue (used to project future ELV COGS). */
    elvRatio: number;
    /** Sum of currently-active recurring expense templates. */
    opexCents: number;
    monthlyGrowthRate: number;
    monthlyChurnRate: number;
    stripeFeeRate: number;
    yearlyExpenseInflation: number;
  };
}

/** Unified SieveGuard-credit pricing (super-admin control plane). */
export interface CreditConfig {
  costs: {
    detection: number;
    emailVerification: number;
    blacklist: number;
    inboxPlacement: number;
    phoneValidation: number;
    domainHealth: number;
  };
  referenceSaleCentsPer1000: number;
  /** Master switch: legacy per-feature quotas (false) vs the single unified wallet (true). */
  enabled: boolean;
  /**
   * One-shot credits granted to a new tenant on registration under wallet mode.
   * Replaces the legacy `trialCredits` + `trialAnomalyCredits` from PlatformSettings —
   * those still apply when `enabled` is off. Warmup is never spent from this pool
   * (per-mailbox Stripe billing, no credit path). 0 = disable the grant entirely.
   */
  trialWalletCredits: number;
}

export interface PnlAssumptions {
  id: string;
  /** Stored as decimal strings server-side; parse with `Number()`. */
  monthlyGrowthRate: string;
  monthlyChurnRate: string;
  expenseInflationYearly: string;
  elvCostPerCredit: string;
  /** Realised Veriphone cost per credit (€/lookup). */
  veriphoneCostPerCredit: string;
  stripeFeeRate: string;
  forecastYears: number;
}

export type TaxBucket = 'home' | 'eu_b2b' | 'eu_b2c' | 'uk' | 'non_eu' | 'unknown';

export type ChatSenderType = 'tenant' | 'admin';

export interface ChatMessage {
  id: string;
  threadId: string;
  tenantId: string;
  senderType: ChatSenderType;
  senderEmail: string;
  body: string;
  sentAt: string;
  readAt: string | null;
  /** Opaque pagination cursor (message createdAt). Echo back as ?before / ?after. */
  cursor: string;
}

export interface ChatThread {
  id: string;
  tenantId: string;
  lastMessageAt: string | null;
  tenantUnread: number;
  adminUnread: number;
}

export interface AdminChatThread extends ChatThread {
  tenantName: string;
  tenantSlug: string;
  lastMessagePreview: string | null;
  lastMessageSender: ChatSenderType | null;
}

export interface ChatHistory {
  thread: ChatThread;
  messages: ChatMessage[];
  /** True when older messages exist before the oldest message in this page. */
  hasMore: boolean;
}

/** Payload pushed over the chat SSE stream. Mirrors api/src/chat/chat.service.ChatEvent. */
export interface ChatEvent {
  type: 'message' | 'read';
  threadId: string;
  tenantId: string;
  message?: ChatMessage;
  side?: ChatSenderType;
}

export interface AdminTenantSummaryRow {
  id: string;
  name: string;
  slug: string;
  country: string | null;
  vatId: string | null;
  status: string;
  billingInterval: string | null;
  planId: string | null;
  planName: string | null;
  /** True when the current subscription was minted by a super-admin (no Stripe). */
  grantedByAdmin: boolean;
  monthlyAmountCents: number;
  yearlyAmountCents: number | null;
  annualRevenueCents: number;
  taxBucket: TaxBucket;
  subscriptionStartedAt: string | null;
  createdAt: string;
  trialExtraDays: number;
  trialCreditsBonus: number;
  trialAnomalyCreditsBonus: number;
  /** Permanent bonus credits — trial roll-over + manual super-admin grants. */
  bonusVerifierCredits: number;
  bonusAnomalyCredits: number;
  /** When the one-time trial-to-subscription roll-over fired (null if not yet). */
  trialRolledOverAt: string | null;
  /** True when the workspace is in agency mode (manages child workspaces). */
  isAgency: boolean;
  /**
   * Per-agency negotiated discount ladder. Three states:
   *   - null  → pending review (agency portal is locked behind the
   *             "book a call" screen until super-admin sets a ladder).
   *   - []    → reviewed, explicit "no discount". Portal is unlocked.
   *   - [...] → per-agency ladder of breakpoints; the engine picks the
   *             largest tier the agency currently meets.
   * Only meaningful when isAgency = true.
   */
  agencyDiscountOverrideTiers: AgencyDiscountTier[] | null;
  /** Super-admin note on the negotiation (audit trail). */
  agencyDiscountOverrideNote: string | null;
  /**
   * Per-tenant detection routing override. Null on the row means the tenant is on
   * the platform defaults — the path 99% of tenants stay on. Surfaced here so the
   * admin tenants table can show a badge on the row without a second roundtrip.
   * Never returned through any tenant-facing endpoint.
   */
  detectionConfig: DetectionConfig | null;
}

export interface ElvUsageByEndpoint {
  endpoint: string;
  calls: number;
  credits: number;
  cached: number;
  estimatedCostCents: number;
  creditsPerCall: number;
}

export interface ElvUsageDaily {
  day: string;
  endpoint: string;
  credits: number;
}

export interface ElvUsageByTenant {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  endpoint: string;
  credits: number;
  estimatedCostCents: number;
}

export interface ElvUsage {
  rangeDays: number;
  elvCostPerCredit: number;
  totals: {
    calls: number;
    credits: number;
    cached: number;
    estimatedCostCents: number;
  };
  byEndpoint: ElvUsageByEndpoint[];
  daily: ElvUsageDaily[];
  byTenant: ElvUsageByTenant[];
}

/** Generalised per-provider burn (drives both /admin/elv-usage and /admin/veriphone-usage). */
export interface ProviderUsage extends ElvUsage {
  providers: string[];
  costPerCredit: number;
  providerCosts: {
    elvCostPerCredit: number;
    veriphoneCostPerCredit: number;
  };
}

/** Veriphone live credit pool (from /admin/analytics/veriphone/refresh-credits). */
export interface VeriphoneCredits {
  status: 'success' | 'auth-failed';
  counter: number;
  limit: number;
  payg: number;
  plan: string | null;
  renew: string | null;
  active: boolean;
  last_reset: string | null;
}

/** Veriphone subscription tier reference (mirrors VERIPHONE_PLAN_TIERS on the API). */
export interface VeriphonePlanTier {
  name: string;
  monthlyCredits: number;
  monthlyPriceUsdCents: number;
  monthlyPriceEurCents: number;
}

/** Veriphone PAYG pack reference (mirrors VERIPHONE_PAYG_TIERS on the API). */
export interface VeriphonePackTier {
  credits: number;
  priceUsdCents: number;
  priceEurCents: number;
}

export interface VeriphoneRecommendation {
  monthlyConsumption: number;
  currentPlan: VeriphonePlanTier | null;
  recommendedPlan: VeriphonePlanTier | null;
  recommendedPack: VeriphonePackTier;
  strategy: 'payg' | 'subscription';
  annualSavingsCents: number;
  recommendedCostPerCredit: number;
  reason: string;
}

export interface VeriphoneRunway {
  pool: number | null;
  currentPlan: string | null;
  dailyConsumption: number;
  monthToDate: number;
  monthlyConsumption: number;
  daysOfRunway: number | null;
  alertThresholdDays: number;
  belowAlertThreshold: boolean;
  recommendation: VeriphoneRecommendation;
  catalog: {
    plans: VeriphonePlanTier[];
    paygPacks: VeriphonePackTier[];
  };
}

/**
 * Super-admin AI cost telemetry rollup — mirrors the API's `AiUsageReport`.
 * Drives `/admin/ai-usage`. Cents are derived from `costMicrosUsd / 10_000`
 * server-side so the frontend never has to do the math.
 */
export interface AiUsageReport {
  rangeDays: number;
  totals: {
    calls: number;
    inputTokens: number;
    outputTokens: number;
    costMicrosUsd: number;
    costUsdCents: number;
  };
  byProvider: Array<{
    provider: string;
    calls: number;
    inputTokens: number;
    outputTokens: number;
    costMicrosUsd: number;
    costUsdCents: number;
    share: number;
  }>;
  byModel: Array<{
    provider: string;
    model: string;
    calls: number;
    costMicrosUsd: number;
    costUsdCents: number;
    avgCostMicros: number;
  }>;
  byPurpose: Array<{
    purpose: string;
    calls: number;
    costMicrosUsd: number;
    costUsdCents: number;
  }>;
  bySource: Array<{
    source: string;
    calls: number;
    costMicrosUsd: number;
    costUsdCents: number;
    avgLatencyMs: number;
  }>;
  byTenant: Array<{
    tenantId: string;
    tenantName: string;
    tenantSlug: string;
    calls: number;
    costMicrosUsd: number;
    costUsdCents: number;
  }>;
  daily: Array<{
    day: string;
    costMicrosUsd: number;
    byProvider: Record<string, number>;
  }>;
}

export interface AdminTenantsSummary {
  homeCountry: string;
  tenants: AdminTenantSummaryRow[];
  totals: { tenants: number; paying: number; annualRevenueCents: number };
  byBucket: Record<TaxBucket, { count: number; annualRevenueCents: number }>;
  ossThresholdEur: number;
  ossUsageEur: number;
  ossUsagePct: number;
  stripeTaxRecommended: boolean;
  stripeTaxReasons: string[];
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  description: string | null;
  active: boolean;
  createdAt: string;
}

export interface CreatedWebhook {
  id: string;
  url: string;
  events: string[];
  secret: string;
}

export interface WebhookDelivery {
  id: string;
  event: string;
  status: 'pending' | 'success' | 'failed';
  attempts: number;
  responseStatus: number | null;
  url: string;
  lastError: string | null;
  createdAt: string;
}

export interface ApiRequestLogEntry {
  id: string;
  method: string;
  path: string;
  status: number;
  durationMs: number;
  apiKeyId: string | null;
  requestBody: unknown;
  responseBody: unknown;
  createdAt: string;
}

export interface ClusterSummary {
  id: string;
  stem: string;
  memberCount: number;
  maxScore: number;
  action: Action;
}

/** Cluster + its member records — returned by `GET /detection/clusters/:id`. */
export interface ClusterDetail extends ClusterSummary {
  createdAt: string;
  members: Array<{
    id: string;
    email: string;
    canonical: string | null;
    score: number;
    action: Action;
    disposition: Disposition;
    reasons: string[];
    verificationStatus: VerificationStatus | null;
    createdAt: string;
  }>;
}

/** Lightweight per-record Domain Health snapshot embedded in records + record-detail. */
export interface RecordDomainHealth {
  score: number;
  spfStatus: 'present' | 'missing' | 'multiple' | 'invalid';
  dkimStatus: 'present' | 'missing';
  dmarcStatus: 'present' | 'missing' | 'invalid';
}

/** Single row of the Records page (`GET /detection/records`). Matches the API response. */
export interface RecordListItem {
  id: string;
  email: string;
  canonical: string | null;
  score: number;
  action: Action;
  reasons: string[];
  disposition: Disposition;
  nearCount: number;
  clusterId: string | null;
  verificationStatus: VerificationStatus | null;
  domain: string | null;
  /** Null when the workspace doesn't have Domain Health access OR the domain has no snapshot yet. */
  domainHealth: RecordDomainHealth | null;
  createdAt: string;
}

/** Filter shape passed to `GET /detection/records`. */
export interface RecordsQuery {
  listId?: string;
  action?: Action;
  disposition?: Disposition;
  verification?: VerificationStatus | 'none';
  clusterId?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface PlanQuotas {
  emailsPerMonth?: number | null;
  lists?: number | null;
  apiKeys?: number | null;
}

export type PlanKind = 'basic' | 'verify';

export interface Plan {
  id: string;
  name: string;
  description: string | null;
  kind: PlanKind;
  monthlyAmountCents: number;
  yearlyAmountCents: number | null;
  currency: string;
  /** Mailbox-verification cap. 0 = anomaly-only (Basic-tier). */
  verifierCredits: number;
  /** Unified-wallet monthly allowance (Phase 4). 0 = no wallet allowance from this plan. */
  monthlyCredits: number;
  /** Phase 4F — bundle Warmup into the plan price; bypasses per-mailbox billing. */
  includesWarmup: boolean;
  /** Phase 4F — number of warmup mailboxes bundled into the plan price (0 when not bundled). */
  warmupInboxesPerMonth: number;
  quotas: PlanQuotas;
  active: boolean;
  stripeMonthlyPriceId: string | null;
  stripeYearlyPriceId: string | null;
}

export type BillingInterval = 'month' | 'year';

export type PaymentState = 'processing' | 'succeeded' | 'failed';

export interface CurrentSubscription {
  status: string | null;
  interval: BillingInterval | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** Last seen payment-attempt state. Distinct from `status` — SEPA can be `processing` while sub is `active`. */
  paymentState: PaymentState | null;
  lastPaymentErrorMessage: string | null;
  lastPaymentEventAt: string | null;
  /** False after a SEPA mandate has been revoked — tenant must re-authorise. */
  mandateActive: boolean;
  /**
   * True while the tenant has never had a yearly subscription. Drives the
   * "2 months free on your first year" badge on the yearly toggle; once the first
   * yearly sub is confirmed by Stripe, this flips to false and the yearly toggle
   * shows the regular "save 10%" copy.
   */
  firstYearCouponAvailable: boolean;
  plan: {
    id: string;
    name: string;
    monthlyAmountCents: number;
    yearlyAmountCents: number | null;
    verifierCredits: number;
    quotas: PlanQuotas;
  } | null;
}

export interface CreditStatus {
  allowance: number | null;
  used: number;
  pct: number | null;
  paidProviderActive: boolean;
  exhausted: boolean;
  /**
   * Roll-over / super-admin-granted bonus pool. `granted` = lifetime grant;
   * `remaining` = what's still spendable after lifetime overflow over the monthly
   * plan allowance. Consumed AFTER the monthly plan budget runs out.
   */
  bonus: { granted: number; remaining: number };
}

export interface AnomalyCreditStatus {
  allowance: number;
  used: number;
  pct: number | null;
  exhausted: boolean;
  bonus: { granted: number; remaining: number };
}

export interface Entitlements {
  quotas: PlanQuotas;
  usage: { apiKeys: number; lists: number; emailsThisMonth: number };
  /** Mailbox-level verification budget (the "verifier credits" counter). */
  credits: CreditStatus;
  /** Anomaly-only screening budget — bonus on Verify plans, standalone on trial. */
  anomalyCredits: AnomalyCreditStatus;
  /** Platform-level ingest cut-offs (tenant-readable, super-admin tuned). */
  ingest: { asyncThreshold: number };
  /**
   * AI availability — true when the super-admin has connected at least one
   * provider (Anthropic or Gemini). When false the portal hides every AI
   * affordance: cards, the review-suggestion banner, the chat "AI" button,
   * the onboarding wizard, etc. Drives the global gate in `useAiAvailable()`.
   */
  ai: { available: boolean };
}

export interface Analytics {
  /** Lifetime totals — what the big stat tiles show. */
  processed: number;
  actions: { allow: number; verify: number; review: number };
  flagged: number;
  dispositions: { accepted: number; pending: number; declined: number };
  verification: Record<string, number>;
  /** Top reason buckets across every record. */
  reasons: Array<{ category: string; count: number }>;
  clusters: { total: number; suspect: number };
  /** 30-day per-day series with action breakdown — drives the stacked chart + tooltip. */
  daily: Array<{
    day: string;
    total: number;
    allow: number;
    verify: number;
    review: number;
  }>;
  /** Current 30d window vs prior 30d window — drives the delta badges on stat tiles. */
  window: {
    current: { processed: number; verify: number; review: number };
    previous: { processed: number; verify: number; review: number };
  };
  /** Per-list breakdown — empty when the request is scoped to one list. */
  byList: Array<{
    id: string;
    name: string;
    slug: string;
    processed: number;
    review: number;
    verify: number;
  }>;
}

export interface AuditEntry {
  id: string;
  action: string;
  actorType: string;
  actorId: string | null;
  resource: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

/**
 * A cross-tenant audit row for the super-admin platform-wide audit feed. `tenant` is null
 * for platform-level actions with no owning tenant — affiliate and super-admin-member
 * sign-ups / logins / management. The page badges those by scope instead of tenant name.
 */
export interface AdminAuditEntry extends AuditEntry {
  tenant: { id: string; name: string; slug: string } | null;
}
